import React, { useContext } from 'react';
import { Icon } from '@iconify/react';
import { CircularProgress } from '@mui/material';
import { TextsContext } from '../../context/textsContext';
import style from '../../scss/saveFavoritePlan.module.scss';

/**
 * Estados posibles de la validación de nombre (los produce el consumidor,
 * p. ej. `SaveFavoritePlanDialog` a partir del debounce + `useFavoritePlanNameExists`).
 */
export const NAME_FEEDBACK_STATUS = {
    IDLE: 'idle',
    CHECKING: 'checking',
    AVAILABLE: 'available',
    TAKEN: 'taken',
    FAILED: 'failed',
    INVALID_LENGTH: 'invalid_length',
    INVALID_CHARS: 'invalid_chars',
};

/**
 * Línea de feedback que se muestra bajo un input mientras se valida el nombre
 * contra el back. Ocupa alto fijo (ver `.nameFeedback`) para que el contenedor
 * no salte al cambiar de estado; en `idle` se renderiza vacía por lo mismo.
 *
 * @param {string} status  uno de `NAME_FEEDBACK_STATUS`
 */
const NameFeedback = ({ status }) => {
    const { texts } = useContext(TextsContext);

    if (status === NAME_FEEDBACK_STATUS.IDLE) return <span className={style.nameFeedback} />;

    const feedbackByStatus = {
        [NAME_FEEDBACK_STATUS.CHECKING]: {
            className: style.nameChecking,
            icon: <CircularProgress size={12} className="me-1" style={{ color: '#595959' }} />,
            text: texts?.favorite_plan_save_name_checking ?? 'Checking name availability…',
        },
        [NAME_FEEDBACK_STATUS.AVAILABLE]: {
            className: style.nameAvailable,
            icon: <Icon icon="mdi:check-circle-outline" className="me-1" />,
            text: texts?.favorite_plan_save_name_available ?? 'Name available',
        },
        [NAME_FEEDBACK_STATUS.TAKEN]: {
            className: style.nameTaken,
            icon: <Icon icon="mdi:alert-circle-outline" className="me-1" />,
            text: texts?.favorite_plan_save_name_taken ?? 'This name is already in use',
        },
        [NAME_FEEDBACK_STATUS.FAILED]: {
            className: style.nameTaken,
            icon: <Icon icon="mdi:alert-circle-outline" className="me-1" />,
            text: texts?.favorite_plan_save_name_check_failed ?? 'We couldn’t check this name',
        },
        [NAME_FEEDBACK_STATUS.INVALID_LENGTH]: {
            className: style.nameTaken,
            icon: <Icon icon="mdi:alert-circle-outline" className="me-1" />,
            text: texts?.favorite_plan_save_name_invalid_length ?? 'Name must be between 5 and 50 characters',
        },
        [NAME_FEEDBACK_STATUS.INVALID_CHARS]: {
            className: style.nameTaken,
            icon: <Icon icon="mdi:alert-circle-outline" className="me-1" />,
            text: texts?.favorite_plan_save_name_invalid_chars ?? 'Name must only contain letters and numbers',
        },
    };

    const feedback = feedbackByStatus[status];
    if (!feedback) return <span className={style.nameFeedback} />;

    return (
        <span className={`Poppins ${style.nameFeedback} ${feedback.className}`}>
            {feedback.icon}{feedback.text}
        </span>
    );
};

export default NameFeedback;
